<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <div
    class="min-h-screen bg-[var(--color-bg)] text-[var(--color-text)] flex flex-col">
    <!-- Error Message -->
    <div
      v-if="error"
      class="mx-4 mb-4 p-4 bg-red-100 text-red-800 rounded-lg shadow">
      {{ error }}
    </div>

    <!-- Form Container with ModuleShell -->
    <div
      class="flex-1 overflow-y-auto px-4 pb-24"
      style="padding-bottom: calc(5rem + env(safe-area-inset-bottom))">
      <ModuleShell hideAction>
        <form @submit.prevent="handleSubmit" class="space-y-3">
        <!-- Ear Tag -->
        <div class="flex flex-col">
          <label class="font-semibold mb-1">
            {{ $t('views.goats.goatForm.eartag.label') }}
          </label>
          <input
            ref="eartagInput"
            v-model="formData.eartag"
            type="text"
            :placeholder="$t('views.goats.goatForm.eartag.placeholder')"
            @focus="positionCursorAtEnd"
            @input="handleEartagInput"
            maxlength="17"
            class="w-full p-3 rounded-lg border border-[var(--color-primary-400)] bg-[var(--color-primary-100)] text-[var(--color-text)] font-mono text-lg tracking-wider focus:outline-none focus:ring-2 focus:ring-[var(--color-highlight)]" />
          <!-- Eartag format help -->
          <span class="text-xs text-[var(--color-text)] mt-1">
            {{ $t('views.goats.goatForm.eartag.formatHelper') }}
          </span>
          <!-- Live status -->
          <div v-if="formData.eartag && isValidEartagFormat(formData.eartag) && !validationErrors.eartag" class="flex items-center gap-2 text-green-600 text-sm mt-1">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
            </svg>
            <span>{{ $t('views.goats.goatForm.eartag.valid') }}</span>
          </div>
          <!-- Error Message -->
          <span
            v-if="
              (validationErrors.eartag === $t('views.goats.goatForm.eartag.duplicate')) ||
              (submitted && validationErrors.eartag === $t('views.goats.goatForm.eartag.invalid'))
            "
            class="text-red-500 text-xs mt-2 block">
            {{ validationErrors.eartag }}
          </span>
        </div>

        <!-- Name -->
        <div class="flex flex-col">
          <label class="font-semibold mb-1">
            {{ $t('views.goats.goatForm.name.label') }}
          </label>
          <input
            v-model="formData.name"
            type="text"
            :placeholder="$t('views.goats.goatForm.name.placeholder')"
            class="w-full p-3 rounded-lg border border-[var(--color-primary-400)] bg-[var(--color-primary-100)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-highlight)]" />
        </div>

        <!-- Date of Birth -->
        <div class="flex flex-col">
          <label class="font-semibold mb-1">
            {{ $t('views.goats.goatForm.dateOfBirth.label') }} *
          </label>
          <input
            v-model="formData.dateOfBirth"
            type="date"
            required
            :max="new Date().toISOString().split('T')[0]"
            class="w-full p-3 rounded-lg border border-[var(--color-primary-400)] bg-[var(--color-primary-100)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-highlight)]" />
          <span
            v-if="validationErrors.dateOfBirth"
            class="text-red-500 text-xs mt-1">
            {{ validationErrors.dateOfBirth }}
          </span>
        </div>

        <!-- Place of Birth -->
        <div class="flex flex-col">
          <label class="font-semibold mb-1">
            {{ $t('views.goats.goatForm.placeOfBirth.label') }}
          </label>
          <input
            v-model="formData.placeOfBirth"
            type="text"
            :placeholder="$t('views.goats.goatForm.placeOfBirth.placeholder')"
            class="w-full p-3 rounded-lg border border-[var(--color-primary-400)] bg-[var(--color-primary-100)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-highlight)]" />
        </div>

        <!-- Breed -->
        <div class="flex flex-col">
          <label class="font-semibold mb-1">
            {{ $t('views.goats.goatForm.breed.label') }}
          </label>
          <select
            v-model="formData.breed"
            class="dropdown-select w-full p-3 pr-10 rounded-lg border border-[var(--color-primary-400)] bg-[var(--color-primary-100)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-highlight)]">
            <option value="" disabled hidden>{{ $t('views.goats.goatForm.breed.selectbreed') }}</option>
            <option :value="null">
              {{ $t('views.goats.goatForm.breed.noBreed') }}
            </option>
            <option value="Burenziege">{{ $t('views.goats.goatForm.breed.boerGoat') }}</option>
            <option value="Thüringer Waldziege">{{ $t('views.goats.goatForm.breed.thuringianGoat') }}</option>
            <option value="Anglo Nubier Ziege">{{ $t('views.goats.goatForm.breed.angloNubianGoat') }}</option>
            <option value="Bunte Deutsche Edelziege">{{ $t('views.goats.goatForm.breed.germanAlpineDairyGoat') }}</option>
            <option value="Pfauenziege">{{ $t('views.goats.goatForm.breed.peacockGoat') }}</option>
            <option value="Tauernschecke">{{ $t('views.goats.goatForm.breed.tauernsheckGoat') }}</option>
            <option value="Weisse Deutsche Edelziege">{{ $t('views.goats.goatForm.breed.germanImporvedWhiteGoat') }}</option>
            <option value="Walliser Schwarzhalsziege">{{ $t('views.goats.goatForm.breed.valaisBlackneckGoat') }}</option>
            <option value="Kreuzung">{{ $t('views.goats.goatForm.breed.crossbreedGoat') }}</option>
            </select>
        </div>

        <!-- Gender -->
        <div class="flex flex-col">
          <label class="font-semibold mb-1">
            {{ $t('views.goats.goatForm.gender') }} *
          </label>
          <div class="flex gap-3">
            <button
              type="button"
              @click="formData.gender = 'Female'"
              :class="[
                'flex-1 p-3 rounded-lg border font-semibold text-base transition-all duration-200',
                formData.gender === 'Female' 
                  ? 'border-black dark:border-white bg-[var(--color-highlight)] text-white shadow-md'
                  : 'border-[var(--color-primary-400)] bg-[var(--color-primary-100)] text-[var(--color-text)] hover:bg-[var(--color-primary-200)]'
              ]">
              {{ $t('views.goats.goatForm.female') }}
            </button>
            <button
              type="button"
              @click="formData.gender = 'Male'"
              :class="[
                'flex-1 p-3 rounded-lg border font-semibold text-base transition-all duration-200',
                formData.gender === 'Male' 
                  ? 'border-black dark:border-white bg-[var(--color-highlight)] text-white shadow-md' 
                  : 'border-[var(--color-primary-400)] bg-[var(--color-primary-100)] text-[var(--color-text)] hover:bg-[var(--color-primary-200)]'
              ]">
              {{ $t('views.goats.goatForm.male') }}
            </button>
          </div>
          <span
            v-if="validationErrors.gender"
            class="text-red-500 text-xs mt-1 block">
            {{ validationErrors.gender }}
          </span>
        </div>

        <!-- Group -->
        <div class="flex flex-col">
          <label class="font-semibold mb-1">
            {{ $t('views.goats.goatForm.group') }}
          </label>

          <!-- Loading State -->
          <div
            v-if="groupsLoading"
            class="w-full p-3 rounded-lg border border-[var(--color-primary-400)] text-center opacity-70 bg-[var(--color-primary-100)] text-[var(--color-text)]">
            <span class="text-sm">Loading groups...</span>
          </div>

          <!-- Dropdown -->
          <select
            v-else
            v-model="formData.groupId"
            class="dropdown-select w-full p-3 pr-10 rounded-lg border border-[var(--color-primary-400)] bg-[var(--color-primary-100)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-highlight)]">
            <option :value="null">
              {{ $t('views.goats.goatForm.noGroup') }}
            </option>
            <option
              v-for="group in availableGroups"
              :key="group.id"
              :value="group.id">
              {{ group.name }}{{ group.location ? ` - ${group.location}` : '' }}
            </option>
          </select>
        </div>

        <!-- Action Buttons -->
        <div class="flex gap-3 pt-4">
          <button
            type="button"
            @click="handleCancel"
            class="flex-1 p-4 rounded-lg border border-[var(--color-primary-400)] bg-[var(--color-primary-100)] text-[var(--color-text)] hover:bg-[var(--color-primary-200)] transition-colors duration-200 font-semibold">
            {{ $t('common.buttons.cancel') }}
          </button>

          <button
            type="submit"
            :disabled="loading"
            class="flex-1 h-14 min-w-[140px] px-4 rounded-lg font-semibold transition-all duration-200 flex flex-col justify-center items-center whitespace-normal text-center"
            style="background-color: var(--color-success); border: 1px solid var(--color-success); color: #fff; font-size: 16px; font-weight: 700;"
            @mouseenter="if (!loading) { $event.target.style.backgroundColor = 'var(--color-success-dark)'; $event.target.style.borderColor = 'var(--color-success-dark)'; $event.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)' }"
            @mouseleave="$event.target.style.backgroundColor = 'var(--color-success)'; $event.target.style.borderColor = 'var(--color-success)'; $event.target.style.boxShadow = 'none'; $event.target.style.transform = 'none'"
            @mousedown="if (!loading) { $event.target.style.transform = 'scale(0.97)'; $event.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)' }"
            @mouseup="if (!loading) { $event.target.style.transform = 'none'; $event.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)' }">
            {{
              loading
                ? $t('views.goats.goatForm.saving')
                : $t('views.goats.goatForm.submit')
            }}
          </button>
        </div>
      </form>
    </ModuleShell>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted } from 'vue'
  import { useRouter } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import { useGoatStore } from '@/stores/goat.store.js'
  import { useGroupStore } from '@/stores/group.store.js'
  import { useUiStore } from '@/stores/ui.store.js'
  import { storeToRefs } from 'pinia'
  import ModuleShell from '@/components/ui/layout/ModuleShell.vue'

  const router = useRouter()
  const { t } = useI18n()
  const goatStore = useGoatStore()
  const groupStore = useGroupStore()
  const uiStore = useUiStore()

  // Get reactive state from stores
  const { loading, error: storeError } = storeToRefs(goatStore)
  const { groups: availableGroups, loading: groupsLoading } =
    storeToRefs(groupStore)
  const { addGoat, clearError } = goatStore
  const { fetchGroups } = groupStore
  const { queueToast } = uiStore

  // Local state
  const formData = ref({
    eartag: '',
    name: '',
    dateOfBirth: '',
    placeOfBirth: '',
    breed: '',
    gender: '',
    groupId: null,
  })

  const validationErrors = ref({})
  const error = ref(null)
  const eartagInput = ref(null)
  const submitted = ref(false)

  /**
   * Position cursor at the end of eartag input
   */
  function positionCursorAtEnd(event) {
    const input = event.target
    const length = input.value.length
    setTimeout(() => {
      input.setSelectionRange(length, length)
    }, 0)
  }

  /**
   * Format eartag as DE XXXX XXX XXXXX (4-3-5 digit pattern)
   */
  function formatEartag(input) {
    // Remove all spaces and convert to uppercase
    const cleaned = input.replace(/\s/g, '').toUpperCase()
    
    // Limit to DE + 12 digits
    const limited = cleaned.substring(0, 14) // DE + 12 digits
    
    // Build formatted string
    let formatted = ''
    
    // Add "DE" prefix (first 2 chars)
    if (limited.length > 0) {
      formatted = limited.substring(0, 2)
    }
    
    // Add remaining digits with specific spacing: DE XXXX XXX XXXXX
    if (limited.length > 2) {
      const digits = limited.substring(2)
      formatted += ' '
      
      // First 4 digits
      for (let i = 0; i < Math.min(4, digits.length); i++) {
        formatted += digits[i]
      }
      
      // Space before next 3 digits
      if (digits.length > 4) {
        formatted += ' '
        for (let i = 4; i < Math.min(7, digits.length); i++) {
          formatted += digits[i]
        }
      }
      
      // Space before last 5 digits
      if (digits.length > 7) {
        formatted += ' '
        for (let i = 7; i < digits.length; i++) {
          formatted += digits[i]
        }
      }
    }
    
    return formatted
  }

  /**
   * Validate full eartag format - must be DE followed by exactly 12 digits
   */
  function isValidEartagFormat(eartag) {
    if (!eartag) return false;
    const cleaned = eartag.replace(/\s/g, '');
    const pattern = /^DE\d{12}$/;
    return pattern.test(cleaned);
  }

  /**
   * Check for duplicate eartag in existing goats
   */
  function isDuplicateEartag(eartag) {
    const cleaned = eartag.replace(/\s/g, '');
    return goatStore.goats.some(g => g.eartag && g.eartag.replace(/\s/g, '') === cleaned);
  }

  /**
   * Handle eartag input with auto-formatting and live validation
   */
  function handleEartagInput(event) {
    const input = event.target.value;
    const formatted = formatEartag(input);
    formData.value.eartag = formatted;
    
    // Only show duplicate error live, not format error
    if (isDuplicateEartag(formatted)) {
      validationErrors.value.eartag = t('views.goats.goatForm.eartag.duplicate');
    } else {
      // Only clear error if it was duplicate or format error
      if (
        validationErrors.value.eartag === t('views.goats.goatForm.eartag.duplicate') ||
        validationErrors.value.eartag === t('views.goats.goatForm.eartag.invalid')
      ) {
        delete validationErrors.value.eartag;
      }
    }
  }

  // ==========================================
  // LIFECYCLE: FETCH HERDS ON MOUNT
  // ==========================================

  onMounted(async () => {
    console.log('NewGoatView mounted')

    // Set global header
    uiStore.setHeader({
      title: t('views.goats.goatForm.title'),
      showBack: true
    })

    // Fetch groups if not already loaded
    if (availableGroups.value.length === 0) {
      console.log('Fetching groups for dropdown...')
      await fetchGroups()
    }

    console.log(`Found ${availableGroups.value.length} groups`)
    
    // Pre-fill eartag with prefix if it exists (user can delete it if not needed)
    if (uiStore.hasEartagPrefix() && !formData.value.eartag) {
      formData.value.eartag = uiStore.getEartagPrefix() + ' '
    }
  })

  /**
   * Validate form data
   */
  function validateForm() {
    validationErrors.value = {};
    let isValid = true;

    // Eartag optional - but validate format and uniqueness if provided
    if (formData.value.eartag && formData.value.eartag.trim()) {
      if (!isValidEartagFormat(formData.value.eartag)) {
        validationErrors.value.eartag = t('views.goats.goatForm.eartag.invalid');
        isValid = false;
      } else if (isDuplicateEartag(formData.value.eartag)) {
        validationErrors.value.eartag = t('views.goats.goatForm.eartag.duplicate');
        isValid = false;
      }
    }
    // Date of birth validation
    if (!formData.value.dateOfBirth) {
      validationErrors.value.dateOfBirth = t('views.goats.goatForm.required');
      isValid = false;
    } else {
      const birthDate = new Date(formData.value.dateOfBirth);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      birthDate.setHours(0, 0, 0, 0);
      if (birthDate > today) {
        validationErrors.value.dateOfBirth = t('views.goats.goatForm.dateInFuture');
        isValid = false;
      }
    }

    // Gender validation
    if (!formData.value.gender || !['Male', 'Female'].includes(formData.value.gender)) {
      validationErrors.value.gender = t('views.goats.goatForm.required');
      isValid = false;
    }

    return isValid;
  }

  /**
   * Handle form submission
   */
  async function handleSubmit() {
    console.log('Form submission started')

    // Mark as submitted
    submitted.value = true

    // Clear previous messages
    error.value = null
    clearError()

    // Validate form
    if (!validateForm()) {
      console.log('Form validation failed:', validationErrors.value)
      return
    }

    console.log('Form validation passed')
    console.log('Saving goat to database...')

    try {
      // Prepare goat data (eartag is already in correct format)
      const goatData = {
        ...formData.value
      }
      
      // Save goat through Pinia store (backend integration)
      const goatId = await addGoat(goatData)

      if (goatId) {
        console.log('Goat saved successfully with ID:', goatId)
        // Queue toast to be shown on the destination view
        queueToast(t('views.goats.goatForm.success'), 'saved')
        // Navigate immediately without delay
        router.push('/goats')
      } else {
        error.value = storeError.value || t('views.goats.goatForm.error')
      }
    } catch (err) {
      console.error('Failed to save goat:', err)
        error.value = t('views.goats.goatForm.error')
    }
  }

  /**
   * Handle cancel button
   */
  function handleCancel() {
    router.back()
  }
</script>

<style scoped>
  /* Style calendar icon for dark mode */
  html[data-theme='dark'] input[type='date']::-webkit-calendar-picker-indicator {
    filter: invert(1);
  }

  /* Dropdown select styling */
  .dropdown-select {
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 0.75rem center;
    background-size: 12px;
  }

  html[data-theme='dark'] .dropdown-select {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23ccc' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  }
</style>
